import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useReducedMotion, useScroll, useSpring, useTransform } from "framer-motion";
import Lenis from "lenis";
import axios from "axios";
import { ArrowUpRight, BookOpen, BriefcaseBusiness, ChevronDown, Globe2, Menu, Users, X } from "lucide-react";
import { Toaster, toast } from "sonner";
import { ASSETS, COPY } from "./siteCopy";
import "@/App.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ICONS = { book: BookOpen, work: BriefcaseBusiness, students: Users };
const EASE = [0.22, 1, 0.36, 1];

const fadeUp = {
  hidden: { opacity: 0, y: 48 },
  show: { opacity: 1, y: 0, transition: { duration: 0.85, ease: EASE } }
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.08 } }
};

function MaskedTitle({ lines, accent = 1 }) {
  return (
    <h1 data-testid="hero-title">
      {lines.map((line, index) => (
        <span className="line-mask" key={line}>
          <motion.span
            className={index === accent ? "accent-line" : ""}
            initial={{ y: "115%", rotate: 2 }}
            animate={{ y: 0, rotate: 0 }}
            transition={{ duration: 0.95, delay: 0.16 + index * 0.13, ease: EASE }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </h1>
  );
}

function SectionTitle({ lines, light = false }) {
  return (
    <h2 className={light ? "section-title light" : "section-title"}>
      {lines.map((line) => <span key={line}>{line}</span>)}
    </h2>
  );
}

function Reveal({ children, className = "", ...props }) {
  return (
    <motion.div className={className} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.25 }} {...props}>
      {children}
    </motion.div>
  );
}

export default function App() {
  const [language, setLanguage] = useState("hi");
  const [menuOpen, setMenuOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const lenisRef = useRef(null);
  const heroRef = useRef(null);
  const reduceMotion = useReducedMotion();
  const t = COPY[language];

  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const rotateX = useSpring(useTransform(mouseY, [-0.5, 0.5], [7, -7]), { stiffness: 120, damping: 18 });
  const rotateY = useSpring(useTransform(mouseX, [-0.5, 0.5], [-8, 8]), { stiffness: 120, damping: 18 });
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroVisualY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const heroCopyY = useTransform(scrollYProgress, [0, 1], [0, -42]);

  useEffect(() => {
    document.documentElement.lang = language === "hi" ? "hi" : "en";
  }, [language]);

  useEffect(() => {
    if (reduceMotion) return undefined;
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true, wheelMultiplier: 0.95 });
    lenisRef.current = lenis;
    let rafId;
    const raf = (time) => { lenis.raf(time); rafId = requestAnimationFrame(raf); };
    rafId = requestAnimationFrame(raf);
    return () => { cancelAnimationFrame(rafId); lenis.destroy(); lenisRef.current = null; };
  }, [reduceMotion]);

  const scrollTo = (id) => {
    const target = document.getElementById(id);
    if (!target) return;
    if (lenisRef.current) lenisRef.current.scrollTo(target, { offset: -76 });
    else target.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  const handleHeroMouse = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    mouseX.set((event.clientX - rect.left) / rect.width - 0.5);
    mouseY.set((event.clientY - rect.top) / rect.height - 0.5);
  };

  const submitVolunteer = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    setLoading(true);
    try {
      await axios.post(`${API}/volunteers`, Object.fromEntries(new FormData(form)));
      form.reset();
      toast.success(t.success);
    } catch (error) {
      console.error("Volunteer submission failed", error);
      toast.error(t.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="site-shell">
      <motion.header className="site-header" data-testid="site-header" initial={{ y: -48, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.7, ease: EASE }}>
        <button className="brand" onClick={() => scrollTo("home")} data-testid="brand-home-button" aria-label="YJP home">
          <img src={ASSETS.logo} alt="Yuva Janniyon Party flag" />
          <span><strong>YJP</strong><small>{t.party}</small></span>
        </button>
        <nav className={menuOpen ? "nav-links open" : "nav-links"} data-testid="main-navigation">
          <button onClick={() => scrollTo("about")} data-testid="nav-about-button">{t.nav[0]}</button>
          <button onClick={() => scrollTo("sankalp")} data-testid="nav-sankalp-button">{t.nav[1]}</button>
          <button onClick={() => scrollTo("campaigns")} data-testid="nav-campaigns-button">{t.nav[2]}</button>
          <button className="nav-join" onClick={() => scrollTo("join")} data-testid="nav-join-button">{t.join} <ArrowUpRight size={16} /></button>
        </nav>
        <div className="header-actions">
          <button className="language-switch" onClick={() => setLanguage(language === "hi" ? "en" : "hi")} data-testid="language-toggle"><Globe2 size={15} /> {t.languageToggle}</button>
          <button className="menu-toggle" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-button" aria-label="Toggle menu">{menuOpen ? <X /> : <Menu />}</button>
        </div>
      </motion.header>

      <section id="home" ref={heroRef} className="hero-section" data-testid="hero-section" onMouseMove={reduceMotion ? undefined : handleHeroMouse}>
        <motion.div className="hero-copy" style={reduceMotion ? {} : { y: heroCopyY }}>
          <motion.div className="eyebrow" initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, ease: EASE }}>
            <span />{t.heroKicker}
          </motion.div>
          <MaskedTitle key={language} lines={t.heroTitle} />
          <motion.p initial={{ opacity: 0, y: 26 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.5, ease: EASE }}>{t.heroText}</motion.p>
          <motion.div className="hero-actions" initial={{ opacity: 0, y: 26 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.64, ease: EASE }}>
            <button className="primary-button" onClick={() => scrollTo("join")} data-testid="hero-join-button">{t.join} <ArrowUpRight size={18} /></button>
            <button className="text-button" onClick={() => scrollTo("sankalp")} data-testid="hero-sankalp-button">{t.manifesto} <ChevronDown size={17} /></button>
          </motion.div>
          <motion.div className="hero-meta" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9, duration: 0.7 }}>{t.heroMeta}</motion.div>
        </motion.div>
        <motion.div className="hero-visual" data-testid="hero-visual" style={reduceMotion ? {} : { y: heroVisualY, rotateX, rotateY }}>
          <div className="flag-spotlight" />
          <motion.div className="flag-card" initial={{ opacity: 0, y: 70, rotate: 4 }} animate={{ opacity: 1, y: 0, rotate: 2 }} transition={{ duration: 1, delay: 0.34, ease: EASE }}>
            <img src={ASSETS.logo} alt="YJP flag and emblem" />
          </motion.div>
          <motion.div className="hero-stamp" initial={{ scale: 0, rotate: -24 }} animate={{ scale: 1, rotate: -12 }} transition={{ duration: 0.7, delay: 0.82, ease: EASE }}>YJP<br /><small>SANKALP</small></motion.div>
          <div className="vertical-label">लोकतंत्र · PARTICIPATION · DEMOCRACY</div>
          <div className="scroll-cue"><ChevronDown size={16} /> SCROLL</div>
        </motion.div>
      </section>

      <div className="slogan-band" data-testid="slogan-banner" aria-label="YJP slogans">
        <div className="marquee-track">
          {[...t.marquee, ...t.marquee, ...t.marquee].map((item, index) => <span key={`${item}-${index}`}>{item}<i>·</i></span>)}
        </div>
      </div>

      <motion.section id="about" className="about-section section-pad" data-testid="about-section" variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.25 }}>
        <Reveal className="section-label">01 / {t.about}</Reveal>
        <div className="about-grid">
          <Reveal><SectionTitle lines={t.aboutTitle} /></Reveal>
          <Reveal className="about-copy">
            <p className="lead-text">{t.aboutLead}</p>
            <p>{t.aboutText}</p>
          </Reveal>
        </div>
        <Reveal className="stats-strip" data-testid="about-stats">
          {t.stats.map(([value, label]) => <div key={label}><strong>{value}</strong><span>{label}</span></div>)}
        </Reveal>
      </motion.section>

      <motion.section className="founder-section section-pad" data-testid="founder-section" variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.25 }}>
        <Reveal className="founder-photo-wrap">
          <img src={ASSETS.founder} alt={language === "hi" ? "युवा जननीति पार्टी के संस्थापक" : "Founder of Yuva Janniyon Party"} data-testid="founder-photo" />
          <span className="founder-badge">YJP<br /><small>{t.founderRole}</small></span>
        </Reveal>
        <div className="founder-copy">
          <Reveal className="section-label">01A / {t.founder}</Reveal>
          <Reveal><SectionTitle lines={t.founderTitle} light /></Reveal>
          <Reveal><p>{t.founderText}</p></Reveal>
          <Reveal className="founder-quote">“{t.founderQuote}”</Reveal>
        </div>
      </motion.section>

      <motion.section className="cofounder-section section-pad" data-testid="cofounder-section" variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.25 }}>
        <div className="cofounder-copy">
          <Reveal className="section-label">01C / {t.cofounder}</Reveal>
          <Reveal><SectionTitle lines={t.cofounderTitle} /></Reveal>
          <Reveal><h3 className="cofounder-name">{t.cofounderName}</h3></Reveal>
          <Reveal><p>{t.cofounderText}</p></Reveal>
          <Reveal className="cofounder-quote">“{t.cofounderQuote}”</Reveal>
        </div>
        <Reveal className="cofounder-photo-wrap">
          <img src={ASSETS.cofounder} alt={language === "hi" ? "आकर्ष सिंह — युवा जननीति पार्टी के सह-संस्थापक" : "Aakarsh Singh — Co-Founder of Yuva Janniyon Party"} data-testid="cofounder-photo" />
          <span className="cofounder-badge">YJP<br /><small>{t.cofounderRole}</small></span>
        </Reveal>
      </motion.section>

      <motion.section className="ideals-section section-pad" data-testid="ideals-section" variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.25 }}>
        <div className="ideals-head">
          <div>
            <Reveal className="section-label">01B / {t.ideals}</Reveal>
            <Reveal><SectionTitle lines={t.idealsTitle} /></Reveal>
          </div>
          <Reveal className="section-note">{t.idealsNote}</Reveal>
        </div>
        <Reveal className="ideals-grid" data-testid="ideals-grid">
          {t.idealCards.map(([initials, name, role, text], index) => (
            <motion.article className="ideal-card" key={name} data-testid={index === 0 ? "ideal-card-atal" : "ideal-card-chandra"} whileHover={reduceMotion ? {} : { y: -7 }} transition={{ duration: 0.3 }}>
              <span className="ideal-initials">{initials}</span>
              <h3>{name}</h3>
              <div className="ideal-role">{role}</div>
              <p>{text}</p>
            </motion.article>
          ))}
        </Reveal>
      </motion.section>

      <motion.section id="sankalp" className="manifesto-section section-pad" data-testid="sankalp-section" variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }}>
        <div className="section-top">
          <div>
            <Reveal className="section-label">02 / {t.manifesto}</Reveal>
            <Reveal><SectionTitle lines={t.sankalpTitle} /></Reveal>
          </div>
          <Reveal className="section-note">{t.sankalpNote}</Reveal>
        </div>
        <Reveal className="pillar-grid" data-testid="sankalp-bento">
          {t.pillars.map(([number, title, text, icon], index) => {
            const Icon = ICONS[icon];
            return (
              <motion.article className={`pillar-card pillar-${index + 1}`} key={number} data-testid={`manifesto-card-${number}`} whileHover={reduceMotion ? {} : { y: -8 }} transition={{ duration: 0.3 }}>
                <div className="pillar-number">{number}</div>
                <Icon size={30} strokeWidth={1.5} />
                <h3>{title}</h3>
                <p>{text}</p>
                <span className="card-arrow">↗</span>
              </motion.article>
            );
          })}
          <div className="bento-image"><img src={ASSETS.campus} alt="Students on a university campus" /><span>{t.bentoImageTitle}</span></div>
          <div className="bento-quote">{t.bentoQuote}</div>
        </Reveal>
      </motion.section>

      <motion.section id="campaigns" className="campaign-section section-pad" data-testid="campaigns-section" variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.25 }}>
        <Reveal className="campaign-image">
          <img src={ASSETS.campaign} alt="Young people participating in a public movement" />
        </Reveal>
        <div className="campaign-copy">
          <Reveal className="section-label">03 / {t.campaigns}</Reveal>
          <Reveal><SectionTitle lines={t.campaignTitle} /></Reveal>
          <Reveal><p>{t.campaignText}</p></Reveal>
          <Reveal><button className="outline-button" onClick={() => scrollTo("join")} data-testid="campaign-join-button">{t.join} <ArrowUpRight size={18} /></button></Reveal>
        </div>
      </motion.section>

      <motion.section id="join" className="join-section section-pad" data-testid="join-section" variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }}>
        <div className="join-intro">
          <Reveal className="section-label">04 / YJP COMMUNITY</Reveal>
          <Reveal><SectionTitle lines={[t.formTitle]} light /></Reveal>
          <Reveal><p>{t.formSub}</p></Reveal>
        </div>
        <Reveal>
          <form onSubmit={submitVolunteer} className="volunteer-form" data-testid="volunteer-form">
            {t.fields.map((label, index) => (
              <label key={label} data-testid={`volunteer-field-${index}`}>
                <span>{label}</span>
                <input data-testid={`volunteer-input-${["name", "mobile", "email", "city"][index]}`} required name={["name", "mobile", "email", "city"][index]} type={index === 2 ? "email" : index === 1 ? "tel" : "text"} placeholder={label} />
              </label>
            ))}
            <button className="primary-button form-submit" disabled={loading} type="submit" data-testid="volunteer-submit-button">{loading ? "..." : t.submit} <ArrowUpRight size={18} /></button>
          </form>
        </Reveal>
      </motion.section>

      <footer className="site-footer" data-testid="site-footer">
        <div><span className="footer-mark">YJP</span><p>{t.footerText}</p></div>
        <div className="footer-right">© 2026 {t.party}<br /><span>{t.footerTag}</span></div>
      </footer>
      <Toaster position="bottom-right" richColors />
    </main>
  );
}
